import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { useToast } from '@/components/ui/toast';
import { useI18n } from '@/lib/i18n';
import { useIslands } from '@/lib/islands';
import { fetchUstawieniaLogow, ustawLogSps, type PlikLogu } from '@/lib/logi-sps-api';

/**
 * PLIKI LOGÓW SPS DO PILNOWANIA — sekcja w ustawieniach wyspy.
 *
 * ⚠️ PO CO TO JEST: awaria SupplyPointa nie zgłasza się w portalu w żaden sposób, bo agent widzi
 * BAZĘ, nie program. Ślad zostaje na maszynie — w `FatalErrors.log` i w znacznikach awarii
 * `AppInfo\APPCRASH_SPSUI_*` — a tam nikt nie zagląda, dopóki nie ma powodu.
 *
 * ⚠️ TRZY KOLUMNY, NIE JEDNA, I TO JEST SEDNO TEGO EKRANU:
 *   • „pilnuj" — czy agent w ogóle patrzy na ten plik (domyślnie wszystko włączone),
 *   • „alarmuj" — czy zmiana zapala alert; domyślnie tylko błędy krytyczne i znaczniki awarii,
 *     bo pliki debug rosną bez przerwy i alert z nich zagłuszyłby ten jeden ważny,
 *   • „treść" — czy agent przysyła DOPISANĄ TREŚĆ. To osobna zgoda, bo treść logu opuszcza wtedy
 *     maszynę klienta i ląduje w naszej bazie; nie ma jej dostawać hurtem razem z pilnowaniem.
 *
 * ⚠️ WIĘKSZOŚCI TYCH PLIKÓW NA MASZYNIE NIE MA i to nie jest awaria — powstają dopiero po włączeniu
 * odpowiedniego debugowania w SPS (zmierzone na bipoincie). Ekran mówi to wprost, żeby nikt nie
 * szukał usterki w dozorze, który poprawnie milczy.
 */
export function PlikiLogowSps() {
  const { t } = useI18n();
  const { groupId } = useIslands();
  const toast = useToast();
  const qc = useQueryClient();

  const { data, isPending } = useQuery({
    queryKey: ['logi-sps-ustawienia', groupId],
    queryFn: () => fetchUstawieniaLogow(groupId!),
    enabled: Boolean(groupId),
  });

  const zapisz = useMutation({
    mutationFn: (v: { klucz: string; monitoruj: boolean; alarmuj: boolean; ogon: boolean }) =>
      ustawLogSps(groupId!, v.klucz, { monitoruj: v.monitoruj, alarmuj: v.alarmuj, ogon: v.ogon }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['logi-sps-ustawienia', groupId] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const przestaw = (p: PlikLogu, pole: 'monitoruj' | 'alarmuj' | 'ogon', wartosc: boolean) => {
    const nowe = { monitoruj: p.monitoruj, alarmuj: p.alarmuj, ogon: p.ogon, [pole]: wartosc };
    /**
     * ⚠️ WYŁĄCZENIE PILNOWANIA GASI TEŻ ALARM I TREŚĆ. Zostawienie ich włączonych dawałoby ekran
     * obiecujący alert z pliku, na który nikt nie patrzy — a to gorsze niż brak alertu.
     */
    if (pole === 'monitoruj' && !wartosc) {
      nowe.alarmuj = false;
      nowe.ogon = false;
    }
    zapisz.mutate({ klucz: p.klucz, monitoruj: nowe.monitoruj, alarmuj: nowe.alarmuj, ogon: nowe.ogon });
  };

  if (!groupId) return null;

  return (
    <section className="space-y-3">
      <div className="space-y-1">
        <h2 className="text-sm font-medium">{t('spsLog.settingsTitle')}</h2>
        {/*
          ⚠️ BEZ DWÓCH AKAPITÓW POD TYTUŁEM — zgłoszone wprost (28.08.2026). Mówiły, po co ten
          ekran jest i że większości plików na maszynie nie ma. Pierwsze wynika z nazwy sekcji,
          drugie widać przy każdym wierszu (plik nieobecny ma to napisane w stanie).
        */}
      </div>

      {isPending || data === undefined ? (
        <p className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="size-4 animate-spin" />
          {t('common.loading')}
        </p>
      ) : (
        <div className="overflow-x-auto rounded-lg border">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-left text-xs text-muted-foreground">
              <tr>
                <th className="p-2">{t('spsLog.file')}</th>
                <th className="p-2 text-center">{t('spsLog.watch')}</th>
                <th className="p-2 text-center">{t('spsLog.alarm')}</th>
                <th className="p-2 text-center">{t('spsLog.tail')}</th>
              </tr>
            </thead>
            <tbody>
              {data.pliki.map((p) => (
                <tr key={p.klucz} className="border-t align-top">
                  <td className="p-2">
                    <span className="font-mono text-[12px]">{p.plik}</span>
                    <br />
                    <span className="text-[11px] text-muted-foreground">
                      {p.opis} · {p.gdzie}
                    </span>
                  </td>
                  <td className="p-2 text-center">
                    <input
                      type="checkbox"
                      aria-label={`${t('spsLog.watch')}: ${p.plik}`}
                      checked={p.monitoruj}
                      disabled={zapisz.isPending}
                      onChange={(e) => przestaw(p, 'monitoruj', e.target.checked)}
                    />
                  </td>
                  <td className="p-2 text-center">
                    <input
                      type="checkbox"
                      aria-label={`${t('spsLog.alarm')}: ${p.plik}`}
                      checked={p.alarmuj}
                      disabled={zapisz.isPending || !p.monitoruj}
                      onChange={(e) => przestaw(p, 'alarmuj', e.target.checked)}
                    />
                  </td>
                  <td className="p-2 text-center">
                    <input
                      type="checkbox"
                      aria-label={`${t('spsLog.tail')}: ${p.plik}`}
                      checked={p.ogon}
                      disabled={zapisz.isPending || !p.monitoruj}
                      onChange={(e) => przestaw(p, 'ogon', e.target.checked)}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <p className="text-xs text-muted-foreground">
        {t('spsLog.retention').replace('{dni}', String(data?.dniPrzechowywania ?? 90))}
      </p>
    </section>
  );
}
